import { FC } from 'react';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/components/ui/chart';
import { useTranslation } from 'react-i18next';
import { DashboardIncomeResponseDto } from '@/features/dashboard/types/dashboard-dto';
import DashboardChartChangeIndicator from '@/features/dashboard/components/DashboardChart/DashboardChartChangeIndicator';
import { DashboardUtils } from '@/features/dashboard/utils/dashboardUtils';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import CurrencyValue from '@/features/finances/components/CurrencyValue';

const MONTH_VALUES = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

type Props = {
	title: 'Income' | 'Occupancy rate' | 'ADR';
	chartData?: DashboardIncomeResponseDto;
	isLoading?: boolean;
	isMobile?: boolean;
};

const DashboardChart: FC<Props> = ({
	title,
	chartData,
	isLoading,
	isMobile,
}) => {
	const { t } = useTranslation();

	const total =
		(title === 'Income' || title === 'ADR') && <CurrencyValue value={chartData?.value} asset='calendar' />;

	const {
		label,
		icon: Icon,
		step,
		type,
		suffix,
		color,
		subColor,
		iconColor,
	} = DashboardUtils.getChartConfig[
		title as keyof typeof DashboardUtils.getChartConfig
	];

	const getMaxDomainValue = (data: { value: number }[], step: number) => {
		const maxValue = Math.max(...data.map((item) => item.value));
		return Math.ceil(maxValue / step) * step;
	};

	const maxYValue = isLoading
		? 100
		: getMaxDomainValue(chartData?.monthlyValues || [], step);

	const loadingData = [
		{
			month: new Date('2025-1-1'),
			value: 60,
		},
		{
			month: new Date('2025-2-1'),
			value: 90,
		},
		{
			month: new Date('2025-3-1'),
			value: 40,
		},
		{
			month: new Date('2025-4-1'),
			value: 60,
		},
		{
			month: new Date('2025-5-1'),
			value: 20,
		},
		{
			month: new Date('2025-6-1'),
			value: 80,
		},
	];

	return (
		<div
			className={cn(
				'p-6 flex grow  flex-col gap-6 border border-popover-100 rounded-2.5xl bg-popover-10 select-none',
				isMobile && 'p-0 border-0 bg-popover-30 gap-4',
			)}>
			{!isMobile && (
				<div className='flex items-center gap-4'>
					<div
						className='size-8 flex items-center justify-center rounded-semi-xl'
						style={{
							backgroundColor: subColor,
						}}>
						<Icon className={iconColor} />
					</div>
					<h4 className='text-xl font-medium'>{t(label)}</h4>
				</div>
			)}
			{isLoading ? (
				<div className='flex flex-col gap-1'>
					<Skeleton className='h-4 w-30 my-2' />
					<Skeleton className='h-3 w-40 my-1' />
				</div>
			) : (
				<div className='flex flex-col gap-1'>
					<span
						className={cn(
							'text-2xl font-medium',
							isMobile && 'text-xl',
						)}>
						{title === 'Income' || title === 'ADR'
							? total
							: `${chartData?.value.toFixed(2) || 0} ${type}`}
					</span>
					<div className='flex items-center gap-1 text-sm'>
						<DashboardChartChangeIndicator
							value={chartData?.difference ?? 0}
						/>
						<span className='text-popover-600'>
							{t('main.dashboard.components.chart.chartVS')}
						</span>
					</div>
				</div>
			)}
			<ChartContainer
				config={DashboardUtils.getChartConfig}
				className='h-64 w-full'>
				<BarChart
					accessibilityLayer
					data={isLoading ? loadingData : chartData?.monthlyValues}
					className={cn(isLoading && 'animate-pulse')}>
					<CartesianGrid strokeDasharray='3 3' />
					<XAxis
						dataKey='month'
						axisLine={{ stroke: '#EAEAEA' }}
						tickLine={{ stroke: '#EAEAEA' }}
						tickMargin={10}
						tick={{ fontSize: 12 }}
						tickFormatter={(value) =>
							MONTH_VALUES[new Date(value).getMonth()]
						}
					/>
					<YAxis
						allowDataOverflow={true}
						scale='linear'
						domain={[0, maxYValue]}
						axisLine={{ stroke: '#EAEAEA' }}
						tickLine={{ stroke: '#EAEAEA' }}
						tickMargin={10}
						tick={{ fontSize: 12 }}
						tickFormatter={(value) => value + suffix}
					/>
					<ChartTooltip content={<ChartTooltipContent />} />
					<Bar
						dataKey='value'
						radius={[20, 20, 0, 0]}
						fill={isLoading ? '#eaeaea' : color}
						maxBarSize={12}
					/>
				</BarChart>
			</ChartContainer>
		</div>
	);
};

export default DashboardChart;
